import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MagicProperty, MagicPropertyBuilder, UpdateMagicPropertyEvent } from './magic-input-models';
import { MatDialog } from '@angular/material/dialog';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { BehaviorSubject, debounceTime, Subscription, tap } from 'rxjs';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { JsonValidator } from '../validators/json-validator';

@Component({
  selector: 'app-magic-input',
  templateUrl: './magic-input.component.html',
  styleUrls: ['./magic-input.component.scss'],
  standalone: false
})
export class MagicInputComponent implements OnInit, OnDestroy {
  inputControl = new FormControl('');

  @Input() magicNestedProperties: MagicProperty[];
  @Input() magicNestedPropertiesAsync$ = new BehaviorSubject<MagicProperty[]>(null);
  
  @Input() removeButtonTooltip: string = 'Delete Input Item';
  get removeButtonTooltipTranslated(): string {
    if(!this.removeButtonTooltip) return '';
    return this.translation.translate(this.removeButtonTooltip, null, this.locale.language)
  }

  @Input() startingValue: string;
  @Input() placeholder: string;
  @Input() showHint = true;
  @Input() showDeleteButton = false;
  @Input() useTextArea = false;
  @Input() useJsonValidator = false;
  @Output() valueChange = new EventEmitter<UpdateMagicPropertyEvent>();
  @Output() removeItem = new EventEmitter<void>();
  @Input() resetEvent: EventEmitter<string>;
  @ViewChild('magicVariablesDialog') magicDialog: TemplateRef<any>;
  @ViewChild('textareaEl') textareaEl: ElementRef<HTMLTextAreaElement>;

  treeControl = new NestedTreeControl<MagicProperty>(node => node.children);
  dataSource = new MatTreeNestedDataSource<MagicProperty>();
  hasChild = (_: number, node: MagicProperty) => !!node.children && node.children.length > 0;

  suggestions: string[] = [];
  cursorPosition = 0;
  listView: boolean = true;
  treeView: boolean = false;
  panelView: boolean = false;
  jsonInvalid: boolean;
  jsonError: string;

  jsonValidatorEnabled = true;
  get shouldValidateJson(): boolean {
    return this.useTextArea && this.useJsonValidator && this.jsonValidatorEnabled;
  }

  constructor(
    public dialog: MatDialog,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private translation: L10nTranslationService,
  ) { }

  subscription = new Subscription();
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    if (this.startingValue !== undefined) {
      this.inputControl.setValue(this.startingValue, { emitEvent: false });
      this.cursorPosition = this.startingValue.length + 1;
      if (this.shouldValidateJson) {
        this.validateJsonValue(this.startingValue);
      }
    }

    this.subscription.add(
      this.inputControl.valueChanges.pipe(
        debounceTime(500),
        tap(value => this.onValueUpdated(value))
      ).subscribe());

    this.subscription.add(
      this.magicNestedPropertiesAsync$
        .subscribe(values => this.updateTreeData(values)));

    if (this.resetEvent) {
      this.subscription.add(
        // reset to the provided value or an empty string
        this.resetEvent.subscribe(value => this.inputControl.reset(value || '')));
    }

    this.updateTreeData(this.magicNestedProperties);
  }

  private onValueUpdated(value: string) {
    let valid = true;
    if (this.shouldValidateJson) {
      const validation = this.validateJsonValue(value);
      valid = validation.valid;
      value = validation.value;
    }
    this.valueChange.emit({ value, valid });
  }

  private updateTreeData(values: MagicProperty[]) {
    if (values?.length) {
      this.magicNestedProperties = values;
      this.dataSource.data = this.magicNestedProperties;
      const parents = this.dataSource.data.filter(x => x.level === 0);
      if (parents?.length) {
        parents.forEach(p => this.treeControl.expand(p));
      }
    }
  }

  private formatMagicVariable(magicVariable: string) {
    return MagicPropertyBuilder.formatMagicVariable(magicVariable);
  }

  private validateJsonValue(value: string): { valid: boolean, value: string } {
    this.jsonError = null;
    this.jsonInvalid = false;
    if (value) {
      const jsonValidationResult = JsonValidator.validateJsonValue(value, 'MagicInputTextArea', this.textareaEl?.nativeElement);
      this.jsonError = jsonValidationResult.errorMessage;
      this.jsonInvalid = !jsonValidationResult.valid;
      return jsonValidationResult;
    }
    return { valid: true, value };
  }

  launchDialog() {
    this.dialog.open(this.magicDialog,
      { width: '100%', maxWidth: '600px', autoFocus: false });
  }

  insertMagicVariable(value: string): void {
    const currentText = this.inputControl.value || '';
    const beforeCursor = currentText.slice(0, this.cursorPosition);
    const afterCursor = currentText.slice(this.cursorPosition);

    value = this.formatMagicVariable(value);
    const newText = `${beforeCursor}${value}${afterCursor}`;
    this.inputControl.setValue(newText);

    // Reset cursor position
    this.cursorPosition += value.length + 1;
    this.dialog.closeAll();
  }

  setCursorPositionOnKeyEvent(event: KeyboardEvent): void {
    // Track the cursor position
    const target = event.target as HTMLInputElement;
    this.cursorPosition = target.selectionStart || 0;
  }

  setCursorPositionOnClickEvent(event: MouseEvent) {
    this.setCursorPosition(event.target as HTMLInputElement)
  }

  setCursorPosition(target: HTMLInputElement): void {
    this.cursorPosition = target?.selectionStart || 0;
  }

  onToggleValidation() {
    this.onValueUpdated(this.inputControl.value);
  }

  manualValidationCheck() {
    this.validateJsonValue(this.inputControl.value);
  }

  onDeleteClick() {
    this.removeItem.emit();
  }
}
